import type { SectorId } from './types';

/** Machine-readable canonical GHG calculation contract (CP-WP-016 base, CP-WP-017 all-sector extension). */
export const CANONICAL_GHG_CONTRACT = {
  version: 'cp-ghg-contract-2.0.0',
  priorVersion: 'cp-ghg-contract-1.0.0',
  methodVersions: {
    solid_waste: 'provisional-county-landfill-factor-v1',
    solid_waste_factor_cap_year: 2019,
    /** @deprecated Use wastewaterVersions.approvedCanonicalCalculationMethod */
    wastewater: 'wastewater-1.4',
    wastewater_ef: 'EF-WW-1.0',
    /** @deprecated Flat carry-forward replaced by annual activity scaling (wastewater-1.4). */
    wastewater_carry_forward_years: [2021, 2022, 2023],
    wastewater_carry_forward_source_year: 2020,
    wastewaterVersions: {
      methodologyDocumentVersion: '1.4',
      approvedCanonicalCalculationMethod: 'wastewater-1.4',
      emissionFactorVersion: 'EF-WW-1.0',
      legacyCalculationMethods: ['wastewater-1.0', '1.2-county', 'wastewater-1.5', 'wastewater-1.3'],
      datasetGenerationVersion: null as string | null,
    },
    transportation_light_duty: 'ldv-emfac2025-county-v1',
    transportation_heavy_duty: 'hdv-emfac2025-fleet-v1',
    transportation_heavy_duty_backcast: 'hdv-emfac2025-county-backcast-v1',
    offroad: 'offroad-direct-sum-v1',
    stationary_energy_electricity: 'buildings-energy-ci-v1',
    stationary_energy_natural_gas: 'buildings-energy-epa-therm-v1',
    agriculture: 'agriculture-county-inventory-v1',
    contractVersion: 'cp-ghg-contract-2.0.0',
    legacyPrecalcVersion: 'v1.3.0',
    canonicalPrecalcVersion: 'v1.4.0-software-source',
  },
  canonicalPublicSurface: 'live_place_ghg_page',
  inventoryYears: { min: 2005, max: 2023 },
  sectors: {
    included: [
      'stationary_energy_electricity_residential',
      'stationary_energy_electricity_commercial',
      'stationary_energy_natural_gas_residential',
      'stationary_energy_natural_gas_commercial',
      'transportation_light_duty',
      'transportation_heavy_duty',
      'solid_waste',
      'wastewater',
      'offroad',
      'agriculture',
    ] as SectorId[],
    wastewater_boundary: {
      include: [
        'direct_wastewater_treatment_ch4',
        'treatment_process_n2o',
        'effluent_n2o',
        'septic_system_emissions',
      ],
      exclude: [
        'treatment_electricity_and_fuels_in_stationary_energy',
        'biosolids_digestion_and_downstream_ch4',
        'avoided_emissions_and_offsets',
      ],
    },
    solid_waste_separate_from_wastewater: true,
  },
  missingDataBehavior: {
    null_to_zero: false,
    unavailable_representation: 'null_or_gap',
    estimated_must_be_labeled: true,
    inventory_completeness_states: ['complete', 'complete_with_estimates', 'partial'],
    partial_when_wastewater_unavailable: true,
    no_county_or_statewide_wastewater_substitution: true,
  },
  wastewaterEstimationHierarchy: [
    'same_year_measured_or_calculated',
    'annual_activity_model_from_nearest_verified',
    'unavailable_when_activity_drivers_missing',
  ],
  rounding: { emissionsMt: 'round_to_integer_for_chart' },
  precalcRule:
    'ghg_inventory_precalculated must be generated from the same contract via --software-source or shared sector modules in canonicalGhgInventory/',
  sectorArchitecture: {
    liveAndCandidateShareModules: true,
    deprecatedPaths: [
      'populate_ghg_inventory --legacy-batch-v1.3 HDV (emfac_final_estimation / emfac2021 backcast) — removed; HDV always heavyDutySector',
      'populate_ghg_inventory --legacy-batch-v1.3 offroad (emissions column, no geography_type) — removed; offroad always offroadSector',
      'populate_ghg_inventory --legacy-batch-v1.3 electricity/ng via processEnergyData (divergent from buildings chart path)',
      'populate_ghg_inventory --legacy-batch-v1.3 LDV via precalculateVMT (divergent from live VMT path)',
    ],
    authorizedProductionRegenerationCommand:
      'npm run populate-ghg-inventory -- --software-source --all-years  (or default without --legacy-batch-v1.3)',
    legacyBatchOptInFlag: '--legacy-batch-v1.3',
    defaultPopulatePath: 'software-source (cp-ghg-contract-2.0.0)',
    canonicalModules: {
      solid_waste: 'solidWasteSector.ts',
      wastewater: 'wastewaterInventoryResolver.ts',
      offroad: 'offroadSector.ts',
      transportation_heavy_duty: 'heavyDutySector.ts',
      transportation_light_duty: 'emfacInventoryLdvFactors.ts + vmtDataService',
      stationary_energy: 'buildingsEnergyDataService.ts',
      agriculture: 'agricultureEmissionsInventory.ts',
    },
  },
  presentationRules: {
    solid_waste_and_wastewater_separate: true,
    optional_parent_subtotal: 'Waste & Wastewater',
    parent_subtotal_derived_from_children: true,
    public_implementation_deferred_to: 'CP-WP-006',
  },
} as const;
